import { z } from 'zod';
import { UnifiedSourceSchema } from '../social-opportunities/social-opportunities.data.schema';
import { insightReasoning, opportunityEngagement, } from '../social-opportunities/social-opportunities.widget.schema';
// OVERVIEW
export const SocialBriefItemOverviewSchema = z.object({
    opportunityTitle: z.string().describe('Title of the selected opportunity.'),
    opportunityDescription: z
        .string()
        .describe('Description/summary of the opportunity (plain text, e.g., from the identifier).'),
    trendSummary: z
        .object({
        title: z.string().describe('Title summarizing the main trend (plain text)'),
        dateDetected: z
            .string()
            .describe('Date the trend was detected (e.g., "2025-01-01" or ISO string)'),
        description: z.string().describe('Short description of the trend (plain text)'),
        sourceLinks: z.array(z.string()).describe('Links to source posts (URLs to social media)'),
    })
        .describe('Trend Summary'),
    whyMattersToBrand: z
        .object({
        brandRelevance: z
            .string()
            .describe('Explanation of why the trend is relevant to the brand (plain text)'),
        insightReasonings: z
            .array(insightReasoning)
            .describe('Array of reasonings explaining why the insight was generated. Provides context and justification for the insight to support marketing decisions.'),
    })
        .describe('Why It Matters to the Brand'),
    trendOrigin: z
        .object({
        trendInsight: z
            .string()
            .describe("Summary of the key insight for this opportunity, often highlighting trending terms or conversation themes with specific metrics. Example: \"Terms like 'family-safe tech', 'kids in the backseat' and 'hands-free driving with kids' have surged 20%.\" Used to quickly communicate the main finding to marketers."),
        sources: z
            .record(z.string().describe('Source key'), UnifiedSourceSchema)
            .describe('Normalized source data indexed by source key - eliminates duplication'),
        engagement: opportunityEngagement.describe('Consolidated engagement data'),
    })
        .describe('Trend Origin details'),
});
// CUSTOMER UNDERSTANDING
export const SocialBriefItemCustomerUnderstandingSchema = z
    .object({
    targetAudienceOrDemandSpace: z
        .array(z.string())
        .describe('List of detected audience segments or demand spaces (plain text, bullet points)'),
    culturalContext: z
        .array(z.string())
        .describe('List of relevant pop culture or event anchors (plain text, bullet points)'),
})
    .describe('Customer Understanding');
// BRAND CONTEXT
export const SocialBriefItemBrandContextSchema = z
    .object({
    brandMentions: z
        .array(z.object({
        label: z
            .string()
            .describe('Brand or nameplate label (plain text, e.g., "Chevrolet Silverado")'),
        description: z.string().describe('Description/context for the mention (plain text)'),
        source: z
            .string()
            .describe('Source link (URL to social media post or RAG document name)'),
    }))
        .describe('Brand Mentions (objects with label, description, and source)'),
    competitiveMentions: z
        .array(z.object({
        label: z.string().describe('Competing brand label (plain text, e.g., "Ford")'),
        description: z.string().describe('Description/context for the mention (plain text)'),
        source: z
            .string()
            .describe('Source link (URL to social media post or RAG document name)'),
    }))
        .describe('Other, competing brands referenced (objects with label, description, and source)'),
    keyMessages: z
        .array(z.object({
        label: z.string().describe('Key message label (plain text, e.g., "Super Cruise")'),
        description: z.string().describe('Message content (plain text)'),
        source: z
            .string()
            .describe('Source link (URL to social media post or RAG document name)'),
    }))
        .describe("Client's POV or alignment to planned messaging (objects with label, description, and source)"),
    toneOfVoice: z
        .object({
        styles: z
            .array(z.string())
            .describe('List of style descriptors for brand tone (plain text, bullet points)'),
        creativeGuidance: z
            .array(z.string())
            .describe('List of creative guidance instructions (plain text, bullet points)'),
    })
        .describe('Tone of voice, including styles and creative guidance'),
})
    .describe('Brand Context');
// MEASUREMENT & ACTIVATION
export const SocialBriefItemMeasurementActivationSchema = z
    .object({
    platformRecommendation: z
        .object({
        primary: z
            .string()
            .describe('Primary platform recommendation (plain text, e.g., "Reddit")'),
        secondary: z
            .string()
            .describe('Secondary platform recommendation (plain text, e.g., "Instagram Reels, TikTok")'),
        rationales: z
            .array(z.string())
            .describe('Rationales for platform choices (plain text, bullet points)'),
    })
        .describe('Platform recommendation details'),
    assumptionsOrFlags: z
        .array(z.string())
        .describe('List of missing assets, approvals, or flags (plain text, bullet points)'),
    assetTypeNeeds: z
        .array(z.string())
        .describe('List of asset type needs (plain text, bullet points)'),
})
    .describe('Measurement & Activation');
// CREATIVE BRIEF SNAPSHOT
export const SocialBriefItemCreativeBriefSnapshotSchema = z
    .object({
    suggestedMessagingStarters: z
        .array(z.string())
        .describe('List of suggested messaging starters (plain text, bullet points)'),
    recommendedFormats: z
        .array(z.string())
        .describe('List of recommended content formats (plain text, bullet points)'),
    references: z
        .array(z.string())
        .describe('References & Citations. List of plain text strings, e.g., document names ("Chevrolet Brand Reference Guide 8/13/24"), post summaries, or URLs.'),
    citationTags: z
        .array(z.object({
        label: z
            .string()
            .describe('Citation tag/topic label, e.g., "Chevy Silverado buyer discussion", "Gen Z first car advice, Camaro/Mustang debate"'),
        url: z
            .string()
            .describe('URL for tag/topic, e.g., "https://reddit.com/comments/1ltg0ak"'),
    }))
        .describe('Citation tags/topics. List of objects with label and url, representing linked social posts or RAG documents.'),
})
    .describe('Creative Brief Snapshot');
export const SocialBriefItemSchema = z.object({
    brandName: z.string().describe('Brand name for the brief (plain text, e.g., "Chevrolet")'),
    overview: SocialBriefItemOverviewSchema,
    customerUnderstanding: SocialBriefItemCustomerUnderstandingSchema,
    brandContext: SocialBriefItemBrandContextSchema,
    measurementActivation: SocialBriefItemMeasurementActivationSchema,
    creativeBriefSnapshot: SocialBriefItemCreativeBriefSnapshotSchema,
});
export const SocialBriefWidgetKey = 'social-brief-widget:v1';
export const SocialBriefWidgetKind = 'brief';
export const SocialBriefWidgetSchema = z.object({
    items: z
        .array(SocialBriefItemSchema.describe('The social brief to show given the selected opportunity'))
        .length(1)
        .describe('Array of social brief items'),
});
